import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { LuMoveRight } from "react-icons/lu";
import LazyImage from "../../common/LazyImage";

const AZURE_BASE_URL =
  "https://hlineweb-f6dwh8btedejg7fz.z03.azurefd.net//hlinefiles/HLineWeb/Projects/assets/products/";

const PRODUCT_IMAGES = {
  "windows-doors": [`${AZURE_BASE_URL}windows/window.jpg`],
  "louvers-system": [`${AZURE_BASE_URL}ralings/railing-system.JPG`],
  railings: [`${AZURE_BASE_URL}louvers/louvers.jpg`],
};

const CATEGORIES = [
  {
    id: "windows-doors",
    name: "Windows & Door",
    blurb:
      "Slim aluminium profiles with high acoustic & thermal performance. Fixed, casement, tilt–turn, corner windows and more.",
  },
  {
    id: "louvers-system",
    name: "Louvers Systems",
    blurb:
      "Entrance, French & sliding systems engineered for heavy use, weather resistance and smooth operation.",
  },
  {
    id: "railings",
    name: "Railing Systems",
    blurb:
      "Frameless & post glass railings for balconies and stairs—clean aesthetics with safety certifications.",
  },
];

function ImgWithFallback({ srcList = [], alt, className }) {
  const [idx, setIdx] = useState(0);

  const sources = srcList.length ? srcList : ["/placeholder.jpg"];

  const handleError = () => setIdx((i) => Math.min(i + 1, sources.length - 1));

  return (
    <img
      src={sources[idx]}
      onError={handleError}
      alt={alt}
      className={className}
      loading="lazy"
    />
  );
}

export default function Products() {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.includes("/products")) {
      const handleScroll = () => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth" });
      };

      const timeout = setTimeout(handleScroll, 800);

      return () => clearTimeout(timeout);
    }
  }, [location.pathname]);

  const handleCardClick = (catId) => {
    navigate(`/products?cat=${encodeURIComponent(catId)}`);
  };

  return (
    <section
      id="products"
      ref={sectionRef}
      className="container bg-transparent relative scroll-mt-24 py-10 overflow-visible"
    >
      <div className="w-full">
        <p className="text-[11px] md:text-xs tracking-[0.28em] uppercase text-white/70">
          Products
        </p>
        <h2 className="mt-3 text-3xl md:text-5xl font-extrabold text-white leading-tight">
          Windows, doors & architectural systems.
        </h2>
        <p className="mt-4 text-white/80 md:text-lg leading-relaxed max-w-3xl">
          Explore our core categories. Click a tile to view subcategories and
          product details on the Products page.
        </p>
        <div className="mt-8 flex gap-3">
          <button
            onClick={() => navigate("/products")}
            className="inline-flex items-center gap-3 justify-center px-5 py-3 rounded-md border border-white/20 text-white hover:bg-white/10"
          >
            View All Products <LuMoveRight className="w-5 h-5" />
          </button>
        </div>
        <div className="mt-10 grid gap-2 grid-cols-2 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORIES.map((c) => (
            <motion.div
              key={c.id}
              onClick={() => handleCardClick(c.id)}
              className="group relative h-[300px] sm:h-[400px] lg:h-[500px] w-full overflow-hidden border border-white/10 bg-white/[0.04] cursor-pointer"
              whileHover={{ scale: 1.01 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
            >
              <ImgWithFallback
                srcList={PRODUCT_IMAGES[c.id]}
                alt={c.name}
                className="absolute inset-0 h-full w-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-[0.8]"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-70 group-hover:opacity-90 transition-all duration-700" />

              <div className="absolute inset-0 flex items-end p-8">
                <div className="transition-all duration-700 opacity-0 translate-y-6 group-hover:opacity-100 group-hover:translate-y-0">
                  <h3 className="text-2xl font-bold text-white drop-shadow-md mb-3">
                    {c.name}
                  </h3>
                  <p className="text-white/85 text-sm leading-relaxed max-w-md">
                    {c.blurb}
                  </p>
                </div>
              </div>

              <div className="absolute inset-0 border border-white/10 group-hover:border-white/30 transition-all duration-700"></div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
